/**
 * Translating between what a person ticks in the tree and what a questionnaire
 * stores.
 *
 * A questionnaire's scope is two lists: whole topics, and questions named one by
 * one. The server resolves them to a single set of questions, so the UI can
 * offer one selector over the tree and put the answer back into whichever of the
 * two lists says it most economically.
 */
import { allQuestionIds, topicQuestionIds } from "../../lib/tree.ts";
import type { TreeTopic } from "../../api/domain.ts";

/** The two scope lists a questionnaire carries. */
export type Scope = {
  topics: string[];
  questions: string[];
};

/** The questions a stored scope covers, resolved the way the server resolves it. */
export const scopeSelection = (topics: readonly TreeTopic[], scope: Scope): string[] => {
  const byId = new Map(topics.map((topic) => [topic.id, topic]));
  const wanted = new Set(scope.questions);

  for (const id of scope.topics) {
    const topic = byId.get(id);
    if (topic === undefined) continue;
    for (const question of topicQuestionIds(topic)) wanted.add(question);
  }

  // Catalog order for what the tree knows, then anything the tree has lost.
  const known = allQuestionIds(topics).filter((id) => wanted.has(id));
  const strays = [...wanted].filter((id) => !known.includes(id));
  return [...known, ...strays];
};

/**
 * The smallest scope that covers exactly this selection.
 *
 * A topic is worth naming only when all of its questions are ticked: then it
 * stands for the whole topic, and questions later added to it are in scope too,
 * which is what ticking a topic means. Everything else is named question by
 * question. Ids the tree does not know are kept rather than dropped, so a stale
 * selection survives a catalog it no longer matches.
 */
export const scopeOf = (topics: readonly TreeTopic[], selected: readonly string[]): Scope => {
  const wanted = new Set(selected);
  const chosen: string[] = [];
  const covered = new Set<string>();

  for (const topic of topics) {
    const questions = topicQuestionIds(topic);
    if (questions.length === 0 || !questions.every((id) => wanted.has(id))) continue;
    chosen.push(topic.id);
    for (const id of questions) covered.add(id);
  }

  return {
    topics: chosen,
    questions: [...wanted].filter((id) => !covered.has(id)),
  };
};
